/**
 * Persistencia de la conversación del asistente.
 *
 * Qué se guarda: una conversación por cuenta (mensajes, propuestas recientes, la pregunta
 * abierta y las últimas referencias). Los textos del diario y de las notas no se copian:
 * solo sus ids (ver parts.ts).
 * Dónde: en la tabla `meta` de IndexedDB de este dispositivo, con la clave
 * `assistant:conversation:<cuenta>`. `meta` no se sincroniza: la conversación nunca sale
 * del dispositivo.
 * Cuánto: como máximo MAX_MESSAGES mensajes, y caduca tras TTL_MS sin actividad.
 * Cómo se elimina: "Nueva conversación" en el chat, al caducar, al cerrar sesión, al
 * vincular otra cuenta y con "borrar datos locales" (wipeLocalData borra toda `meta`).
 */
import { db, getMeta } from '../db/db';
import { parseConversation, type ConversationState } from './conversation';
import { emptyPreferences, preferencesSchema, type Preferences } from '@habitos/assistant/assistant/preferences';

export const PREFIX = 'assistant:conversation:';
/** 24 horas sin actividad: es contexto de trabajo, no memoria permanente. */
export const TTL_MS = 24 * 60 * 60 * 1000;

/** Propietario de los datos locales: la cuenta con sesión iniciada, o "local" sin cuenta. */
export async function conversationOwner(): Promise<string> {
  return (await getMeta<string | null>('accountUserId', null)) ?? 'local';
}

const keyFor = (owner: string) => `${PREFIX}${owner}`;

export async function loadConversation(owner: string, now: number): Promise<ConversationState | null> {
  const row = await db.meta.get(keyFor(owner));
  if (!row) return null;
  const s = parseConversation(row.value);
  // Estado ilegible, de otra cuenta o caducado: se borra en lugar de usarse.
  if (!s || s.owner !== owner || now - s.updatedAt > TTL_MS) {
    await db.meta.delete(keyFor(owner));
    return null;
  }
  return s;
}

export async function saveConversation(s: ConversationState): Promise<void> {
  await db.meta.put({ key: keyFor(s.owner), value: s });
}

export async function clearConversation(owner: string): Promise<void> {
  await db.meta.delete(keyFor(owner));
}

/**
 * Preferencias recordadas por el asistente, por cuenta. A diferencia de la conversación no
 * caducan: se borran desde Ajustes, al cerrar sesión, al vincular otra cuenta o con
 * "borrar datos locales". Tampoco se sincronizan.
 */
export const PREFS_PREFIX = 'assistant:prefs:';

export async function loadPreferences(owner: string): Promise<Preferences> {
  const row = await db.meta.get(`${PREFS_PREFIX}${owner}`);
  const r = preferencesSchema.safeParse(row?.value);
  return r.success ? r.data : emptyPreferences();
}

export async function savePreferences(owner: string, p: Preferences): Promise<void> {
  await db.meta.put({ key: `${PREFS_PREFIX}${owner}`, value: p });
}

/** Borra las conversaciones y las preferencias de todas las cuentas de este dispositivo. */
export async function clearAllConversations(): Promise<void> {
  const keys = (await db.meta.toCollection().primaryKeys()).filter((k) => String(k).startsWith(PREFIX) || String(k).startsWith(PREFS_PREFIX));
  await db.meta.bulkDelete(keys);
}
